import { AUDIENCE_CHOICES, type Audience } from '../book/genres';
import { Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { keep, recall, useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useFlag } from '../shell/hooks';
import { dayText, todayIn } from '../book/model';
import { BackLink } from '../ui/BackLink';
import { PillMenu } from '../ui/PillMenu';
import { ChipRow, ChoiceChips, FilterChip } from '../ui/bits';
import { ListRow, ListTable, type Col } from '../ui/ListTable';
import { ListFoot } from '../ui/ListFoot';
import { SectionFold, SectionFoldAll, openSectionFold } from '../ui/SectionFold';
import { arriveAt } from '../ui/arrive';
import { DETAILS_FILTERS, DETAILS_KEPT, RECENT_KEPT, RecentList } from '../queue/RecentList';
import { Stepper } from '../ui/Stepper';
import { AddBook } from '../add/AddBook';
import { searchFor, type Found } from '../add/add';
import { everyBook } from '../queue/recent';
import { loadReferenceHeads, type Shelves } from '../shelves/load';
import type { ShelfBook } from '../shelves/model';
import { num } from '../queue/model';
import {
  ALL_ROW_COPIES,
  IMPORT_COPIES,
  IMPORT_LIMIT,
  IMPORT_SHELVES,
  defaultName,
  firstBatch,
  addsCopy,
  copyLabel,
  copyPhrase,
  statesToMark,
  itemsFor,
  joinUp,
  lineOf,
  matchLine,
  needsLiterary,
  onYourList,
  queryOf,
  readPaste,
  ready,
  shelfWords,
  splitUp,
  tidyCase,
  waiting,
  type ImportCopy,
  type ImportShelf,
  type Line,
  type Literary,
  type Row,
} from './importList';
import { lineKey, loadHaveFormats, loadLineMemory, loadPastImports, markImportedStates, renameImport, runImport, undoImport, type ImportResult, type PastImport, type UndoResult } from './importLoad';
import { myBooksTab } from './model';
import './my-books.css';

// My books › Add & import (Parts 4 and 5 step 1; agreed with Kristi Oct 31, mocked up in chat):
// two ways to add (Add a book · Import a list; Scan barcodes, shelf photos, the Libby timeline
// and Goodreads wait in the backlog), Past imports (an undone import is gone), and Import a list
// for a typed or pasted list in four steps: Where from · Type or paste · Check the matches · Where
// they go, then what it did (Kristi, Nov 1: follow board 10 closely).

const LITERARY: { key: Literary; label: string }[] = [
  { key: 'fiction', label: 'Fiction' },
  { key: 'nonfiction', label: 'Nonfiction' },
  { key: 'poetry', label: 'Poetry' },
  { key: 'drama', label: 'Drama' },
];

const foundLine = (f: Found) => [f.authors.join(', '), f.year ? String(f.year) : ''].filter(Boolean).join(' · ');

/** A link on Add & import that sets Recently added's filters asks the page to show them (F11). */
const ARRIVE = 'rl:recent-arrive';

/**
 * "See these books ›" on a past import (filters review F11, Kristi Nov 2): Recently added shows that
 * import only (its Came from chip, any date), with the filters you had kept for "← Back to your
 * filters"; its section opens if folded, and the page scrolls to it.
 */
function viewImport(batch: string, needsShelf = false) {
  if (!recall(RECENT_KEPT, null)) {
    keep(RECENT_KEPT, { filters: recall('queue:filters:recent', {}), when: recall('queue:recent:when', 'recent'), noStatus: recall('queue:recent:nostatus', false) });
  }
  keep('queue:filters:recent', { came_from: { mode: 'show', on: [`import:${batch}`] } });
  keep('queue:recent:when', 'any');
  // From an import's Done page, No shelf yet is on when its books went there (Kristi, Nov 1).
  keep('queue:recent:nostatus', needsShelf);
  window.dispatchEvent(new Event(ARRIVE));
}

/** "Give N a shelf ›" (Kristi, Nov 2): Update details' No shelf yet list, with that import's Came from chip on. */
function shelveImport(batch: string) {
  if (!recall(DETAILS_KEPT, null)) keep(DETAILS_KEPT, { filters: recall(DETAILS_FILTERS, {}) });
  keep(DETAILS_FILTERS, { came_from: { mode: 'show', on: [`import:${batch}`] } });
  keep('queue:review:chip', 'shelf');
  navigate(myBooksTab('review').path);
}

/** The last import's Done page, kept while you look at its books in Recently added (Kristi, Nov 1: Back must return to it). */
interface DoneSnapshot {
  result: ImportResult;
  rows: Row[];
  shelf: ImportShelf;
  name: string;
}
let resumeDone: DoneSnapshot | null = null;

const PAST_SHOWN = 3;

/**
 * Add & import (filters review, Kristi Nov 2: one page, version a): three folding sections, Add books
 * · Past imports (the newest few, then Show more) · Recently added (the whole table, with When,
 * Filters, Sort and Select several), and Collapse all / Expand all in the pinned bar.
 */
export function AddImport({ wide, shelves, readTypes, barSlot = null, toRecent = false }: { wide: boolean; shelves: Shelves; readTypes: string[]; barSlot?: HTMLElement | null; /** Opened at Recently added (its old address). */ toRecent?: boolean }) {
  const [past, setPast] = useState<Past[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(resumeDone !== null);
  const [pastShown, setPastShown] = useState(PAST_SHOWN);
  const [recentCount, setRecentCount] = useState<number | null>(null);
  const [whenSlot, setWhenSlot] = useState<HTMLElement | null>(null);
  // Recently added reads its filters when it's drawn: a link here draws it again with the new ones.
  const [arrivals, setArrivals] = useState(0);
  const addOn = useFlag('book_page.add') === true;
  const load = () =>
    loadPastImports()
      .then(setPast)
      .catch(() => setPast([]));
  useEffect(() => {
    void load();
    window.addEventListener(BOOKS_CHANGED, load);
    return () => window.removeEventListener(BOOKS_CHANGED, load);
  }, []);
  const toRecentSection = () => {
    openSectionFold('recent');
    arriveAt('[data-fold="recent"]');
  };
  useEffect(() => {
    const go = () => {
      setArrivals((n) => n + 1);
      toRecentSection();
    };
    window.addEventListener(ARRIVE, go);
    if (toRecent) toRecentSection();
    return () => window.removeEventListener(ARRIVE, go);
  }, []);

  if (importing)
    return (
      <ImportList
        wide={wide}
        shelves={shelves}
        onClose={() => {
          resumeDone = null;
          setImporting(false);
        }}
        onShow={() => {
          setImporting(false);
          window.setTimeout(toRecentSection, 0);
        }}
      />
    );

  return (
    <div class="ai">
      {barSlot ? createPortal(<SectionFoldAll />, barSlot) : null}
      <p class="queue-description">{myBooksTab('add').description}</p>
      <SectionFold store="my_books:add" id="add" title="Add books">
        {/* The ways to add, as panes you choose (board 8; Kristi, Nov 1): what you need, and how long. */}
        <div class="ai-cards">
          {/* Shown only while Add a book is on (R11: nothing that can't be used). */}
          {addOn ? (
            <button type="button" class="ai-card" onClick={() => setAdding(true)}>
              <span class="ai-card-name">Add a book</span>
              <span class="ai-card-need">
                <b>You need:</b> a title, an author or an ISBN, or a link from Amazon, Audible or Goodreads.
              </span>
              <span class="ai-quiet">Under a minute</span>
            </button>
          ) : null}
          {/* Two equal panes, neither looking chosen (consistency pass R3 P8, Kristi Nov 2). */}
          <button type="button" class="ai-card" onClick={() => setImporting(true)}>
            <span class="ai-card-name">Import a list</span>
            <span class="ai-card-need">
              <b>You need:</b> a list you type, or text you paste: from a note, an email, or a photo of your shelf.
            </span>
            <span class="ai-quiet">About 5 minutes, plus checking the matches · up to {IMPORT_LIMIT} books at a time</span>
          </button>
        </div>
      </SectionFold>

      <SectionFold store="my_books:add" id="past" title="Past imports" count={past?.length} noun={['import', 'imports']}>
        <p class="ai-quiet ai-head-note">Each import is a batch of books added together. Rename it, see its books, or undo it.</p>
        {past === null ? (
          <p class="shelves-quiet">Loading your imports…</p>
        ) : !past.length ? (
          <p class="ai-quiet">No imports yet.</p>
        ) : (
          <>
            {/* The one table (R6), in its own order: a record's time order doesn't sort (the sorting rule). */}
            <ListTable label="Past imports" cols={PAST_COLS} order="Newest first" narrow={!wide} class="ai-past">
              {past.slice(0, pastShown).map((p) => (
                <PastRow key={p.id} p={p} shelfless={shelves.no_status.filter((b) => b.importBatch === p.id).length} onRenamed={() => void load()} />
              ))}
            </ListTable>
            <ListFoot shown={Math.min(pastShown, past.length)} total={past.length} page={PAST_SHOWN} onMore={() => setPastShown(pastShown + 10)} onAll={() => setPastShown(past.length)} onFewer={() => setPastShown(PAST_SHOWN)} />
          </>
        )}
      </SectionFold>

      {/* The heading as on the board (Kristi, Nov 3): its count, and When ▾ at the right. */}
      <SectionFold store="my_books:add" id="recent" title="Recently added" count={recentCount ?? undefined} end={<span class="ra-head-end" ref={setWhenSlot} />}>
        {/* Back to the last import's Done page, when you came from it (Kristi, Nov 1). */}
        {resumeDone ? <BackLink where="your import" onClick={() => setImporting(true)} /> : null}
        <RecentList key={arrivals} wide={wide} shelves={shelves} readTypes={readTypes} view="section" onCount={setRecentCount} headSlot={whenSlot} hideBack={resumeDone !== null} />
      </SectionFold>
      {adding ? createPortal(<AddBook onClose={() => setAdding(false)} />, document.body) : null}
    </div>
  );
}

type Past = PastImport;

/** Past imports' columns (R6 T2, T3): Import · Date · Added · No shelf yet, and no header over the actions. */
const PAST_COLS: Col[] = [
  { label: 'Import', width: 'minmax(0, 2.2fr)', book: true, class: 'ai-past-name' },
  { label: 'Date', width: '84px' },
  { label: 'Added', width: 'minmax(0, 1.6fr)' },
  { label: 'No shelf yet', width: 'minmax(0, 1.2fr)' },
  { width: 'minmax(0, 1.6fr)', class: 'ai-past-links' },
];

/** One import: its name (✎ Rename), when, what it brought, and the way to its books (Kristi, Oct 31). */
function PastRow({ p, shelfless, onRenamed }: { p: PastImport; shelfless: number; onRenamed: () => void }) {
  const notice = useNotice();
  const [editing, setEditing] = useState<string | null>(null);
  const save = async () => {
    if (editing === null) return;
    const before = p.name;
    try {
      await renameImport(p.id, editing);
      setEditing(null);
      onRenamed();
      notice({ message: `Renamed: ${editing.trim()}`, undo: () => void renameImport(p.id, before).then(onRenamed) });
    } catch (e) {
      notice({ message: `Not renamed: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };
  // Undo import stays on Past imports (board 10), after a warning (Kristi, Nov 1).
  const [confirming, setConfirming] = useState(false);
  const undo = async () => {
    try {
      notice({ message: undoneText(await undoImport(p.id)) });
      setConfirming(false);
      onRenamed();
    } catch (e) {
      notice({ message: `Not undone: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };
  const nameCell =
    editing === null ? (
      <span>
        <b>{p.name}</b>{' '}
        <button type="button" class="btn-link ai-rename" aria-label={`Rename ${p.name}`} onClick={() => setEditing(p.name)}>
          ✎ Rename
        </button>
      </span>
    ) : (
      <span class="ai-search">
        <input type="text" aria-label="Import name" maxLength={200} value={editing} onInput={(e) => setEditing((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && void save()} />
        <button type="button" class="btn-main" onClick={() => void save()}>
          Save
        </button>
        <button type="button" class="btn-secondary" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </span>
    );
  const date = p.on ? dayText(p.on) : '';
  // Shown only while some still have none (Kristi, Nov 2): opens Update details' No shelf yet list for this import.
  const shelf = shelfless ? (
    <button type="button" class="btn-link" onClick={() => shelveImport(p.id)}>
      Give {num(shelfless)} a shelf ›
    </button>
  ) : (
    <span class="ai-quiet">All have a shelf</span>
  );
  // Within the page: Recently added, showing this import's books (F11).
  const view = (
    <button type="button" class="btn-link" onClick={() => viewImport(p.id)}>
      See these books ›
    </button>
  );
  const undoButton =
    p.kind === 'typed_list' ? (
      <button type="button" class="btn-secondary" onClick={() => setConfirming(true)}>
        Undo import…
      </button>
    ) : null;
  return (
    <>
      <ListRow
        data-import={p.id}
        cells={[
          nameCell,
          <span class="ai-quiet">{date}</span>,
          broughtText(p),
          shelf,
          <>
            {view}
            {undoButton}
          </>,
        ]}
        phone={{
          book: nameCell,
          control: undoButton,
          line: (
            <span class="ai-past-line">
              {[date, broughtText(p)].filter(Boolean).join(' · ')}
              <span class="ai-past-links">
                {shelf}
                {view}
              </span>
            </span>
          ),
        }}
      />
      {confirming ? (
        <div class="lt-row ai-past-confirm" role="row">
          <span role="cell">
            <UndoConfirm onUndo={() => void undo()} onCancel={() => setConfirming(false)} />
          </span>
        </div>
      ) : null}
    </>
  );
}

/** The warning before Undo import (Kristi, Nov 1). */
function UndoConfirm({ onUndo, onCancel, busy = false }: { onUndo: () => void; onCancel: () => void; busy?: boolean }) {
  return (
    <div class="heads-up ai-confirm" role="alertdialog" aria-label="Undo this import?">
      <b>Undo this import?</b> This deletes the books it added, and the copies it added to books you have. Anything you’ve changed since is kept.
      <span class="ai-confirm-acts">
        <button type="button" class="btn-main" disabled={busy} onClick={onUndo}>
          Yes, undo the import
        </button>
        <button type="button" class="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </span>
    </div>
  );
}

export function broughtText(p: Pick<PastImport, 'books' | 'counts'>): string {
  const c = p.counts;
  const parts = [`${num(p.books)} ${p.books === 1 ? 'book' : 'books'}`];
  if (c.copies) parts.push(`${num(c.copies)} copies`);
  if (c.reads) parts.push(`${num(c.reads)} reads`);
  if (c.copies_added) parts.push(`${num(c.copies_added)} ${c.copies_added === 1 ? 'copy' : 'copies'} added`);
  if (c.left_out) parts.push(`${num(c.left_out)} left out`);
  if (c.not_found) parts.push(`${num(c.not_found)} not found`);
  return parts.join(' · ');
}

type Step = 'from' | 'paste' | 'check' | 'where' | 'done';
/** Check the matches' filter chips (R3 Q3a, Kristi Nov 2: a count line and filter chips, not tiles). */
type Kind = 'needs' | 'matched' | 'on_list' | 'not_found' | 'skipped';
const KINDS: [Kind, string][] = [
  ['needs', 'Needs you'],
  ['matched', 'Matched'],
  ['on_list', 'Already on your list'],
  ['not_found', 'Not found'],
  ['skipped', 'Skipped'],
];

/** Board 10's four steps (Kristi, Nov 1: follow the mockup); a step you've done can be opened again. */
const steps = (src: 'type' | 'paste'): [Exclude<Step, 'done'>, string][] => [
  ['from', 'Where from'],
  ['paste', src === 'type' ? 'Type' : 'Paste'],
  ['check', 'Check the matches'],
  ['where', 'Where they go'],
];

/** Review your entries' columns: # · Your entry, and no header over Edit · Split · Join (R6 T3). */
const ENTRY_COLS: Col[] = [
  { label: '#', width: '34px' },
  { label: 'Your entry', width: 'minmax(0, 1fr)' },
  { width: 'auto' },
];

/** Check the matches' columns (R6): the dot of a row that needs you sits before your row. */
const MATCH_COLS: Col[] = [
  { label: 'Your row', width: 'minmax(0, 1.2fr)', book: true },
  { label: 'Match', width: 'minmax(0, 1.5fr)' },
  { label: 'Check', width: 'minmax(0, 1.9fr)' },
  { label: 'Copy', width: '124px' },
  { label: 'Goes to', width: 'minmax(0, 0.8fr)' },
];

/** Where the list comes from (board 10, step 1; Kristi, Nov 1): typed and pasted are two panes on the top
 * row, then Amazon and another spreadsheet, greyed and untagged until they're built (consistency pass,
 * R11); Goodreads waits until it's built. */
type Source = 'type' | 'paste';
const SOURCES: { key: Source | 'amazon' | 'csv'; name: string; tag: string; gives: string; how: string; on: boolean }[] = [
  {
    key: 'type',
    name: 'A list you type',
    tag: 'Least to map',
    gives: 'Titles, usually with authors. Nothing about format, dates or whether you’ve read it.',
    how: 'Type one book per line, straight into the box.',
    on: true,
  },
  {
    key: 'paste',
    name: 'A list you paste',
    tag: 'Messy is fine',
    gives: 'Text copied from a photo of your shelf, or a list from a note, an email or a spreadsheet column.',
    how: 'Copy the text (Live Text on iPhone, Google Lens on Android for a photo), then paste it.',
    on: true,
  },
  {
    key: 'amazon',
    name: 'Amazon (Kindle)',
    tag: '',
    gives: 'Your Kindle books, with purchase dates and Amazon IDs when the file has them.',
    how: 'Amazon’s Request your data, a book-list browser extension’s file, or your Content Library page.',
    on: false,
  },
  { key: 'csv', name: 'Another spreadsheet (CSV)', tag: '', gives: 'Any columns; you say which is Title, Author, ISBN, Format…', how: 'Save or export it as CSV, then add the file.', on: false },
];

function ImportList({ wide, shelves, onClose, onShow }: { wide: boolean; shelves: Shelves; onClose: () => void; /** Back to the page, at Recently added. */ onShow: () => void }) {
  const notice = useNotice();
  const today = todayIn();
  const r0 = resumeDone;
  const [step, setStep] = useState<Step>(r0 ? 'done' : 'from');
  const [src, setSrc] = useState<Source>('type');
  const [editing, setEditing] = useState<{ seq: number; text: string } | null>(null);
  const [memory, setMemory] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    void loadLineMemory()
      .then(setMemory)
      .catch(() => null);
  }, []);
  const [text, setText] = useState('');
  const [lines, setLines] = useState<Line[] | null>(null);
  // Lines past the first 100 wait here (Kristi: "Match the next 82", nothing to edit), remembered on this device.
  const [rest, setRest] = useRemembered<Line[]>('my_books:import:rest', []);
  // Tips the first time, for typing and for pasting apart (Kristi, Nov 1).
  const [tipsSeen, setTipsSeen] = useRemembered<Partial<Record<Source, boolean>>>('my_books:import:tips:seen', {});
  const [tipsOpen, setTipsOpen] = useState(true);
  const [rows, setRows] = useState<Row[]>(r0?.rows ?? []);
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [shelf, setShelf] = useState<ImportShelf>(r0?.shelf ?? 'none');
  const [audience, setAudience] = useState<Audience>('adult');
  const [name, setName] = useState(r0?.name ?? defaultName(today));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(r0?.result ?? null);
  const [done, setDone] = useState<Row[]>(r0?.rows ?? []);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({ needs: true });
  const [reference, setReference] = useState<ShelfBook[]>([]);
  useEffect(() => {
    void loadReferenceHeads()
      .then(setReference)
      .catch(() => null);
  }, []);
  const mine = useMemo(() => everyBook([...Object.values(shelves), reference]), [shelves, reference]);

  const patch = (seq: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.seq === seq ? { ...r, ...p } : r)));

  // Find each book, three at a time; books already on your list aren't looked up: they ask what to add.
  const match = async (ls: Line[]) => {
    const byId = new Map(mine.map((b) => [b.id, b]));
    const start: Row[] = ls.map((l) => {
      // A line you've imported before is the book it became (Kristi, Nov 1), else a book on your list by its words.
      const remembered = memory.get(lineKey(l.text));
      const on = (remembered ? byId.get(remembered) : undefined) ?? onYourList(l, mine);
      return { ...l, state: on ? 'on_list' : 'matching', found: null, candidates: [], onList: on?.label ?? null, onListBook: on?.id ?? null, confirm: null, have: [], literary: null, copy: 'none' };
    });
    setRows(start);
    setKinds([]);
    setStep('check');
    const ids = start.filter((r) => r.onListBook).map((r) => r.onListBook!);
    if (ids.length)
      void loadHaveFormats(ids)
        .then((m) => setRows((rs) => rs.map((r) => (r.onListBook && m.has(r.onListBook) ? { ...r, have: m.get(r.onListBook) } : r))))
        .catch(() => null);
    const todo = start.filter((r) => r.state === 'matching');
    let i = 0;
    const one = async () => {
      while (i < todo.length) {
        const r = todo[i++]!;
        try {
          const ans = await searchFor(queryOf(r));
          const m = matchLine(r, ans.results ?? []);
          if (m.kind === 'matched') patch(r.seq, { state: 'matched', found: m.found, candidates: m.others, literary: m.found.fiction ? 'fiction' : null });
          else if (m.kind === 'pick') patch(r.seq, { state: 'pick', candidates: m.candidates, more: (ans.results ?? []).slice(0, 12) });
          else patch(r.seq, { state: 'not_found' });
        } catch (e) {
          patch(r.seq, { state: 'error', error: e instanceof Error ? e.message : String(e) });
        }
      }
    };
    await Promise.all([one(), one(), one()]);
  };

  const start = (all: Line[]) => {
    const { now, rest: more } = firstBatch(all, mine);
    setRest(more);
    void match(now.map((l, i) => ({ ...l, seq: i + 1 })));
  };

  const batch = useMemo(() => (lines ? firstBatch(lines, mine) : { now: [], rest: [] }), [lines, mine]);
  // ✎ Edit on a line of the read list (Kristi, Nov 1: editing needed to be closer to hand).
  const saveLine = () => {
    if (!editing || !lines) return;
    const t = editing.text.trim();
    setLines(t ? lines.map((l) => (l.seq === editing.seq ? lineOf(t, l.seq) : l)) : lines.filter((l) => l.seq !== editing.seq).map((l, i) => ({ ...l, seq: i + 1 })));
    setEditing(null);
  };

  const isNotFound = (r: Row) => r.state === 'not_found' || r.state === 'error';
  const counts = {
    all: rows.length,
    matched: rows.filter((r) => ready(r) || (r.state === 'on_list' && r.confirm)).length,
    needs: rows.filter(waiting).length,
    on_list: rows.filter((r) => r.state === 'on_list').length,
    not_found: rows.filter(isNotFound).length,
    skipped: rows.filter((r) => r.state === 'skipped').length,
  };
  const matching = rows.filter((r) => r.state === 'matching').length;
  const isKind = (r: Row, k: Kind) =>
    k === 'matched' ? ready(r) || (r.state === 'on_list' && Boolean(r.confirm)) : k === 'needs' ? waiting(r) : k === 'not_found' ? isNotFound(r) : r.state === k;
  // None on shows every row (R3 P2); several at once show any of them.
  const shown = kinds.length ? rows.filter((r) => kinds.some((k) => isKind(r, k))) : rows;
  const toAdd = rows.filter(ready);
  const copiesToAdd = rows.filter(addsCopy);
  const litMissing = rows.filter(needsLiterary).length;
  const canGo = matching === 0 && toAdd.length + copiesToAdd.length > 0;
  const setAllCopies = (c: ImportCopy) => setRows((rs) => rs.map((r) => ({ ...r, copy: c })));
  const allCopy = rows.length && rows.every((r) => r.copy === rows[0]!.copy) ? rows[0]!.copy : null;
  const where = IMPORT_SHELVES.find((x) => x.key === shelf)!.label;
  const bookWord = (n: number) => `${num(n)} ${n === 1 ? 'book' : 'books'}`;

  const doImport = async () => {
    setBusy(true);
    try {
      const res = await runImport(name.trim() || defaultName(today), itemsFor(rows, shelf, audience));
      // Paper books you no longer have: their copies marked lost or donated now (option a).
      const marks = await markImportedStates(statesToMark(rows, res.added));
      setResult(res);
      setDone(rows);
      setStep('done');
      notice({
        message: `${bookWord(res.counts.added ?? 0)} added ${shelfWords(shelf)}${marks.failed.length ? ` · ${num(marks.failed.length)} not marked lost or donated` : ''}`,
        undo: () => void undoImport(res.batch).then((u) => notice({ message: undoneText(u) })),
      });
    } catch (e) {
      notice({ message: `Not imported: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
    setBusy(false);
  };
  const undo = async () => {
    if (!result) return;
    setBusy(true);
    try {
      notice({ message: undoneText(await undoImport(result.batch)) });
      onClose();
    } catch (e) {
      notice({ message: `Not undone: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
    setBusy(false);
  };

  const order: Step[] = ['from', 'paste', 'check', 'where', 'done'];
  const at = order.indexOf(step);
  const reachable = (k: Step) => step !== 'done' && order.indexOf(k) < at && (k !== 'check' || rows.length > 0);

  // The line beside Next on Check the matches: why you can't go on yet, or what's left out (Kristi, Nov 1).
  const checkLine = matching
    ? { alert: false, text: `Finding the books… ${num(rows.length - matching - counts.on_list)} of ${num(rows.length - counts.on_list)}` }
    : !canGo && litMissing
      ? { alert: true, text: `Select a literary genre to continue: ${litMissing === 1 ? '1 book needs one' : `${num(litMissing)} books need one`}.` }
      : !canGo && counts.needs
        ? { alert: true, text: `Pick a book or confirm a row to continue: ${num(counts.needs)} still need you.` }
        : !canGo
          ? { alert: true, text: 'Nothing to import yet.' }
          : counts.needs
            ? {
                alert: litMissing > 0,
                text: `${num(counts.needs)} still need you${litMissing ? `, ${num(litMissing)} for a literary genre` : ''}: pick a book, a literary genre, or confirm. You can go on; they’re left out until you decide.`,
              }
            : { alert: false, text: counts.not_found ? `Everything else is checked; ${num(counts.not_found)} not found ${counts.not_found === 1 ? 'is' : 'are'} left out.` : 'Everything is checked.' };

  return (
    <div class="ai ai-flow">
      <div class="ai-flow-top">
        <BackLink where="Add & import" onClick={onClose} />
      </div>
      <h3 class="ai-flow-title">Import a list</h3>
      {/* The one stepper (R9 M6): numbered steps on a laptop, "Step 3 of 4 · Check the matches" on a phone.
          Not on the Done page: the steps are behind you there, and its heading says what happened. */}
      {step !== 'done' ? (
        <Stepper
          steps={steps(src).map(([key, name]) => ({ key, name }))}
          current={step}
          onPick={setStep}
          canGo={reachable}
          narrow={!wide}
        />
      ) : null}

      {step === 'from' ? (
        <section class="ai-panel" aria-label="Where from">
          <h4>Where is the list coming from?</h4>
          <div class="ai-sources" role="radiogroup" aria-label="Where from">
            {SOURCES.map((x) => (
              <button
                key={x.key}
                type="button"
                role="radio"
                aria-checked={x.key === src}
                aria-disabled={!x.on}
                class={`ai-source${x.key === src ? ' on' : ''}${x.on ? '' : ' later'}`}
                onClick={() => (x.on ? setSrc(x.key as Source) : undefined)}
              >
                <span class="ai-source-name">
                  {x.name}
                  {x.tag ? ' ' : ''}
                  {x.tag ? <span class="ai-tag">{x.tag}</span> : null}
                </span>
                <span class="ai-source-gives">{x.gives}</span>
                <span class="ai-source-how">
                  <b>How:</b> {x.how}
                </span>
              </button>
            ))}
          </div>
          <div class="ai-actions ai-foot">
            <button type="button" class="btn-secondary" onClick={onClose}>
              Back
            </button>
            <button
              type="button"
              class="btn-main"
              onClick={() => {
                setTipsOpen(!tipsSeen[src]);
                setStep('paste');
              }}
            >
              {src === 'type' ? 'Next: type your list' : 'Next: paste your list'}
            </button>
          </div>
        </section>
      ) : null}

      {step === 'paste' ? (
        <section class="ai-panel" aria-label="Type or paste">
          <h4>
            {src === 'type' ? 'Type your list' : 'Paste your list'}{' '}
            {!tipsOpen ? (
              <button type="button" class="btn-link" onClick={() => setTipsOpen(true)}>
                Tips for best results
              </button>
            ) : null}
          </h4>
          {tipsOpen ? (
            <div class="heads-up ai-tips" role="note" aria-label="Tips for best results">
              <b>Tips for best results</b>
              {src === 'type' ? (
                <ol>
                  <li>One book per line: a title alone, or a title and its author (“Piranesi Susanna Clarke” or “Piranesi by Susanna Clarke”).</li>
                  <li>Any order, any capitals; a number or bullet at the start of a line is fine.</li>
                  <li>
                    You’ll check every match on the next step before anything is saved. Up to {IMPORT_LIMIT} books at a time; the rest wait for the next round.
                  </li>
                </ol>
              ) : (
                <>
                  <p>Pasting text from a photo of your shelf? Your phone can read the spines for you (Live Text on iPhone, Google Lens on Android).</p>
                  <ol>
                    <li>Turn the phone sideways so the spines are level, and photograph a few books at a time.</li>
                    <li>Select the text in the photo and copy it, then paste it here.</li>
                    <li>A list from a note, an email or a spreadsheet column pastes the same way.</li>
                    <li>Each line is read as one book. If a title and its author land on two lines, join them on the next screen.</li>
                    <li>You’ll check every match before anything is saved. Up to {IMPORT_LIMIT} books at a time; the rest wait for the next round.</li>
                  </ol>
                </>
              )}
              <button
                type="button"
                class="btn-secondary"
                onClick={() => {
                  setTipsSeen({ ...tipsSeen, [src]: true });
                  setTipsOpen(false);
                }}
              >
                Got it
              </button>
            </div>
          ) : null}
          {lines === null ? (
            <>
              <label class="ai-paste">
                <span class="visually-hidden">Your list</span>
                <textarea rows={wide ? 12 : 9} placeholder={src === 'type' ? 'Piranesi by Susanna Clarke\nThe Overstory — Richard Powers\nLessons in Chemistry' : 'Paste the text here'} value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
              </label>
              <p class="ai-quiet">One book per line works best: a title alone, or a title and its author, in any order and any capitals. Text copied from a photo is fine.</p>
              <div class="ai-actions ai-foot">
                <button type="button" class="btn-secondary" onClick={() => setStep('from')}>
                  Back
                </button>
                <span class="ai-actions-end">
                  {rest.length ? (
                    <button type="button" class="btn-secondary" onClick={() => start(rest)}>
                      Match the next {num(Math.min(rest.length, IMPORT_LIMIT))} from your last list
                    </button>
                  ) : null}
                  <button type="button" class="btn-main" disabled={!text.trim()} onClick={() => setLines(readPaste(text))}>
                    Review your entries
                  </button>
                </span>
              </div>
            </>
          ) : (
            <>
              <p class="ai-readline">
                Read {num(text.split(/\r?\n/).filter((l) => l.trim()).length)} lines as {bookWord(lines.length)}
                {batch.rest.length
                  ? ` · the first ${IMPORT_LIMIT} new books now${batch.now.length > IMPORT_LIMIT ? ` (plus ${num(batch.now.length - IMPORT_LIMIT)} already on your list)` : ''}; ${num(batch.rest.length)} wait for the next round`
                  : ''}
              </p>
              {/* The one table (R6), in the order of your list: it doesn't sort (the sorting rule). */}
              <ListTable label="Books read from your list" cols={ENTRY_COLS} order="In your list’s sequence" narrow={!wide} class="ai-table">
                {lines.map((l, i) => (
                  <Fragment key={l.seq}>
                    {/* Where this round stops (Kristi, Nov 1: the list showed every line, as if all would go). */}
                    {batch.rest.length && i === batch.now.length ? (
                      <div class="lt-row ai-entry-break" role="row">
                        <span role="cell">These {num(batch.rest.length)} wait for the next round</span>
                      </div>
                    ) : null}
                    <ListRow
                      class="ai-entry"
                      quiet={i >= batch.now.length}
                      cells={[
                        <span class="ai-quiet">{l.seq}</span>,
                        editing?.seq === l.seq ? (
                          <span class="ai-search">
                            <input
                              type="text"
                              aria-label={`Edit line ${l.seq}`}
                              value={editing.text}
                              onInput={(e) => setEditing({ seq: l.seq, text: (e.target as HTMLInputElement).value })}
                              onKeyDown={(e) => e.key === 'Enter' && saveLine()}
                            />
                            <button type="button" class="btn-main" onClick={saveLine}>
                              Save
                            </button>
                            <button type="button" class="btn-secondary" onClick={() => setEditing(null)}>
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <>
                            <span class="ai-title">{tidyCase(l.title)}</span>
                            {l.joined ? <span class="ai-note"> · {l.parts.length} lines joined</span> : null}
                          </>
                        ),
                        <span class="ai-entry-acts">
                          {editing?.seq !== l.seq ? (
                            <button type="button" class="btn-link" aria-label={`Edit ${tidyCase(l.title)}`} onClick={() => setEditing({ seq: l.seq, text: l.text.replace(/ \/ /g, ' ') })}>
                              ✎ Edit
                            </button>
                          ) : null}
                          {l.joined ? (
                            <button type="button" class="btn-link" onClick={() => setLines(splitUp(lines, l.seq))}>
                              Split
                            </button>
                          ) : null}
                          {i > 0 ? (
                            <button type="button" class="btn-link" aria-label={`Join ${tidyCase(l.title)} with the line above`} onClick={() => setLines(joinUp(lines, l.seq))}>
                              Join with the line above
                            </button>
                          ) : null}
                        </span>,
                      ]}
                    />
                  </Fragment>
                ))}
              </ListTable>
              <p class="ai-quiet">✎ Edit a line, or join a title and author a photo split onto two lines. Check the matches looks them up next; nothing is saved before step 4.</p>
              <div class="ai-actions ai-foot">
                <button type="button" class="btn-secondary" onClick={() => setLines(null)}>
                  Edit the text
                </button>
                <button type="button" class="btn-main" disabled={!lines.length} onClick={() => start(lines)}>
                  Next: check the matches
                </button>
              </div>
            </>
          )}
        </section>
      ) : null}

      {step === 'check' ? (
        <section class="ai-panel" aria-label="Check the matches">
          <h4>
            Check the matches <span class="ai-quiet">Every line of your list.</span>
          </h4>
          {/* A count line, then filter chips (R3 Q3a, Kristi Nov 2): none on shows every row; Needs you has the dot. */}
          <p class="ai-countline">
            {num(rows.length)} {rows.length === 1 ? 'row' : 'rows'}
            {counts.needs ? <span class="ai-countline-need"> · {num(counts.needs)} {counts.needs === 1 ? 'needs' : 'need'} you</span> : null}
          </p>
          <ChipRow label="Show">
            {KINDS.map(([k, label]) => (
              <FilterChip
                key={k}
                label={label}
                count={counts[k]}
                need={k === 'needs' && counts.needs ? `Needs you: ${num(counts.needs)} to check` : undefined}
                on={kinds.includes(k)}
                onToggle={() => setKinds((now) => (now.includes(k) ? now.filter((x) => x !== k) : [...now, k]))}
              />
            ))}
          </ChipRow>
          <div class="ai-copyall">
            <ChoiceChips
              class="ai-copyall-row"
              label="What are these?"
              lead={<span class="ai-copyall-q">What are these?</span>}
              choices={ALL_ROW_COPIES.map((c) => [c.key, c.label] as const)}
              value={allCopy}
              onPick={(c) => c && setAllCopies(c)}
            >
              {/* Not a choice, so not a filled chip: quiet words (R3 P5). */}
              {allCopy === null ? <span class="ai-quiet ai-mix">A mix, set per row</span> : null}
            </ChoiceChips>
            <p class="ai-quiet">Your list doesn’t say. Pick one for all, then change any row’s Copy.</p>
          </div>
          {/* The one table (R6), in the order of your list: it doesn't sort (the sorting rule). */}
          <ListTable
            label="Your list, matched"
            cols={MATCH_COLS}
            order="In your list’s sequence"
            narrow={!wide}
            class="ai-rows"
            onClear={kinds.length ? () => setKinds([]) : undefined}
          >
            {shown.map((r) => (
              <RowView key={r.seq} r={r} where={where} patch={(p) => patch(r.seq, p)} />
            ))}
          </ListTable>
          <div class="ai-actions ai-foot">
            <button type="button" class="btn-secondary" onClick={() => setStep('paste')}>
              Back
            </button>
            <span class="ai-actions-end">
              <span class={checkLine.alert ? 'ai-alert' : 'ai-quiet'} role={checkLine.alert ? 'alert' : 'status'}>
                {checkLine.text}
              </span>
              <button type="button" class="btn-main" disabled={!canGo} onClick={() => setStep('where')}>
                Next: where they go
              </button>
            </span>
          </div>
        </section>
      ) : null}

      {step === 'where' ? (
        <section class="ai-panel" aria-label="Where they go">
          <h4>Where they go</h4>
          <ChoiceChips
            class="ai-where"
            label="Put the new books in"
            lead={<span class="ai-copyall-q">Put the new books in:</span>}
            choices={IMPORT_SHELVES.map((x) => [x.key, x.label] as const)}
            value={shelf}
            onPick={(k) => k && setShelf(k)}
          />
          <p class="ai-quiet">
            {shelf === 'none' ? 'They’ll be in Recently added › No shelf yet, to give each a shelf. ' : shelf === 'reference' ? '' : 'At the bottom of the shelf. '}A typed list never says you’ve read something, so nothing is marked Finished.
          </p>
          {/* Adult by default, with a choice for the batch (Kristi, Nov 2); one book's audience changes on its page. */}
          <ChoiceChips
            class="ai-where"
            label="Audience"
            lead={<span class="ai-copyall-q">Audience:</span>}
            choices={AUDIENCE_CHOICES.map((a) => [a.key, a.name] as const)}
            value={audience}
            onPick={(k) => k && setAudience(k)}
          />
          <label class="ai-name">
            <span class="ai-copyall-q">Import batch name</span>
            <input type="text" maxLength={200} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </label>
          <p class="ai-quiet">Shows in Recently added under Came from, and on Past imports with Undo import.</p>
          <p class="ai-willdo">
            Will add <b>{bookWord(toAdd.length)}</b> {shelfWords(shelf)} in the batch “{name.trim() || defaultName(today)}”
            {copiesToAdd.length ? `, and ${num(copiesToAdd.length)} ${copiesToAdd.length === 1 ? 'copy' : 'copies'} to books you already have` : ''}. Books you already have change only where you said so.
            {counts.needs + counts.not_found + counts.skipped ? ` Left out: ${[counts.needs ? `${num(counts.needs)} still needing you` : '', counts.not_found ? `${num(counts.not_found)} not found` : '', counts.skipped ? `${num(counts.skipped)} skipped` : ''].filter(Boolean).join(', ')}.` : ''}
          </p>
          <div class="ai-actions ai-foot">
            <button type="button" class="btn-secondary" onClick={() => setStep('check')}>
              Back
            </button>
            <button type="button" class="btn-main" disabled={busy || !canGo} onClick={() => void doImport()}>
              {toAdd.length ? `Import ${bookWord(toAdd.length)}` : 'Import the list'}
            </button>
          </div>
        </section>
      ) : null}

      {step === 'done' && result ? (
        <DoneView
          result={result}
          rows={done}
          where={where}
          name={name.trim() || defaultName(today)}
          open={open}
          setOpen={setOpen}
          busy={busy}
          onUndo={() => setConfirming(true)}
          confirming={confirming}
          onConfirm={() => void undo()}
          onCancel={() => setConfirming(false)}
          onReview={() => {
            // Kept so "← Back to your import" returns to this Done page (Kristi, Nov 1).
            resumeDone = { result, rows: done, shelf, name: name.trim() || defaultName(today) };
            viewImport(result.batch, shelf === 'none');
            onShow();
          }}
          onClose={onClose}
          rest={rest.length}
          onNext={() => {
            setResult(null);
            setName(defaultName(today));
            start(rest);
          }}
        />
      ) : null}
    </div>
  );
}

function undoneText(u: UndoResult): string {
  const parts = [`${num(u.removed)} ${u.removed === 1 ? 'book' : 'books'} removed`];
  if (u.copies_removed) parts.push(`${num(u.copies_removed)} ${u.copies_removed === 1 ? 'copy' : 'copies'} removed`);
  return `Import undone: ${parts.join(', ')}${u.kept.length ? `. Kept, as you’d changed them: ${u.kept.map((k) => `${k.title} (${k.label})`).join(', ')}` : ''}`;
}

/** What the import did (board 10's last screen; Kristi, Nov 1: closer to the mockup). */
function DoneView({
  result,
  rows,
  where,
  name,
  open,
  setOpen,
  busy,
  onUndo,
  confirming,
  onConfirm,
  onCancel,
  onReview,
  onClose,
  rest,
  onNext,
}: {
  result: ImportResult;
  rows: Row[];
  where: string;
  name: string;
  open: Record<string, boolean>;
  setOpen: (o: Record<string, boolean>) => void;
  busy: boolean;
  onUndo: () => void;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onReview: () => void;
  onClose: () => void;
  rest: number;
  onNext: () => void;
}) {
  const c = result.counts;
  const added = rows.filter((r) => result.added.some((a) => a.seq === r.seq));
  const copies = rows.filter(addsCopy);
  const kept = rows.filter((r) => r.state === 'on_list' && r.confirm === 'keep');
  const needs = rows.filter((r) => waiting(r) || r.state === 'not_found' || r.state === 'error');
  const failed = result.failed;
  const title = (r: Row) => r.found?.title ?? tidyCase(r.title);
  const groups: { key: string; name: string; list: string[]; note?: string }[] = [
    { key: 'added', name: 'Added', list: added.map(title) },
    { key: 'copies', name: 'Copies added to books you have', list: copies.map((r) => `${tidyCase(r.title)}: ${copyPhrase(r.copy)}`) },
    { key: 'needs', name: 'Needs you', list: needs.map((r) => tidyCase(r.title)), note: 'Left out of this import. Paste them again once you know which book, or add them with Add a book.' },
    { key: 'kept', name: 'Left as they were', list: kept.map((r) => tidyCase(r.title)) },
    { key: 'failed', name: 'Couldn’t be added', list: failed.map((f) => `${tidyCase(rows.find((r) => r.seq === f.seq)?.title ?? '')}: ${f.message}`) },
  ].filter((g) => g.list.length);
  const head = [`${num(c.added ?? 0)} ${(c.added ?? 0) === 1 ? 'book' : 'books'} added`];
  if (c.copies_added) head.push(`${num(c.copies_added)} ${c.copies_added === 1 ? 'copy' : 'copies'} added`);
  if (needs.length) head.push(`${num(needs.length)} ${needs.length === 1 ? 'needs' : 'need'} you`);
  return (
    <section class="ai-panel" aria-label="Imported your list">
      <h4>Imported your list</h4>
      <p class="ai-donehead">{head.join(' · ')}</p>
      <div class="ai-groups">
        {groups.map((g) => (
          <div key={g.key} class="ai-group">
            <button type="button" class="ai-group-head" aria-expanded={Boolean(open[g.key])} onClick={() => setOpen({ ...open, [g.key]: !open[g.key] })}>
              <span aria-hidden="true">{open[g.key] ? '▾' : '▸'}</span> <b>{g.name}</b>
              <span class="ai-group-n">{num(g.list.length)}</span>
            </button>
            {open[g.key] ? (
              <div class="ai-group-body">
                {g.note ? <p class="ai-quiet">{g.note}</p> : null}
                <ul>
                  {g.list.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <p class="ai-quiet ai-went">What went where:</p>
      <ul class="ai-wentlist">
        {added.length ? (
          <li>
            ✓ {num(added.length)} {added.length === 1 ? 'book' : 'books'} in {where}
          </li>
        ) : null}
        {copies.map((r) => (
          <li key={r.seq}>
            ✓ {tidyCase(r.title)}: {copyPhrase(r.copy)} added
          </li>
        ))}
        {kept.map((r) => (
          <li key={r.seq}>✓ {tidyCase(r.title)}: left as it was</li>
        ))}
        <li>✓ Import batch “{name}”</li>
      </ul>
      <div class="ai-actions ai-start">
        <button type="button" class="btn-main" onClick={onReview}>
          See them in Recently added
        </button>
        <button type="button" class="btn-link" onClick={onClose}>
          Rename or undo it in Past imports ›
        </button>
        <button type="button" class="btn-secondary" disabled={busy} onClick={onUndo}>
          Undo import…
        </button>
      </div>
      {confirming ? <UndoConfirm busy={busy} onUndo={onConfirm} onCancel={onCancel} /> : <p class="ai-quiet">Undo import removes what this import added and keeps anything you’ve changed since.</p>}
      {rest ? (
        <div class="heads-up ai-more">
          {num(rest)} more from your list.{' '}
          <button type="button" class="btn-secondary" onClick={onNext}>
            Match the next {num(Math.min(rest, IMPORT_LIMIT))}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** A result in another language says so (the search already puts yours first). */
const LANGS: Record<string, string> = { fr: 'French', fre: 'French', fra: 'French', de: 'German', ger: 'German', deu: 'German', es: 'Spanish', spa: 'Spanish', it: 'Italian', ita: 'Italian', nl: 'Dutch', dut: 'Dutch', pt: 'Portuguese', por: 'Portuguese', no: 'Norwegian', nor: 'Norwegian', sv: 'Swedish', swe: 'Swedish', da: 'Danish', dan: 'Danish', ja: 'Japanese', jpn: 'Japanese' };
const langTag = (f: Found) => (f.language && !/^en/i.test(f.language) ? ` · ${LANGS[f.language.toLowerCase()] ?? f.language.toUpperCase()}` : '');

const PILL: Record<string, string> = {
  matched: 'Matched',
  pick: 'Pick the book',
  not_found: 'Not found',
  error: 'Not found',
  on_list: 'On your list · confirm',
  skipped: 'Skipped',
  matching: 'Finding it…',
};

function RowView({ r, where, patch }: { r: Row; where: string; patch: (p: Partial<Row>) => void }) {
  const [search, setSearch] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const again = async () => {
    if (!search?.trim()) return;
    setLooking(true);
    try {
      const ans = await searchFor(search.trim());
      const res = ans.results ?? [];
      if (res.length) patch({ state: 'pick', candidates: res.slice(0, 3), more: res.slice(0, 12) });
      else patch({ state: 'not_found' });
      setSearch(null);
    } catch {
      patch({ state: 'error', error: 'The book search didn’t answer. Try again.' });
    }
    setLooking(false);
  };
  const pick = (f: Found) => patch({ state: 'matched', found: f, candidates: r.candidates.filter((c) => c !== f), literary: f.fiction ? 'fiction' : null });
  // More of what the search found, past the first three (Kristi, Nov 1: the right series, the wrong volume).
  const [showMore, setShowMore] = useState(false);
  const extra = (r.more ?? []).filter((m) => !r.candidates.some((c) => c.key === m.key));
  const needs = waiting(r) || r.state === 'not_found' || r.state === 'error';
  const searchBox =
    search !== null ? (
      <span class="ai-search">
        <input type="text" aria-label={`Search again for ${r.title}`} value={search} onInput={(e) => setSearch((e.target as HTMLInputElement).value)} />
        <button type="button" class="btn-secondary" disabled={looking} onClick={() => void again()}>
          Search
        </button>
      </span>
    ) : null;
  const pill = r.state === 'on_list' && r.confirm ? 'On your list' : PILL[r.state] ?? '';
  const goes =
    r.state === 'skipped' || r.state === 'not_found' || r.state === 'error'
      ? '—'
      : r.state === 'pick'
        ? 'after you pick'
        : r.state === 'matching'
          ? ''
          : r.state === 'on_list'
            ? r.confirm === 'add'
              ? r.copy === 'none'
                ? 'pick a Copy'
                : 'Your book'
              : r.confirm === 'keep'
                ? 'No change'
                : 'after you confirm'
            : r.literary
              ? where
              : 'after its literary genre';
  const b = `B${String(r.onList).padStart(4, '0')}`;
  // What a row that needs you is waiting for, for its dot (R6 T6: the teal edge and the dot, not a tint).
  const need = !needs
    ? undefined
    : `Needs you: ${
        r.state === 'pick'
          ? 'pick the book'
          : r.state === 'on_list'
            ? 'add a copy or leave it'
            : r.state === 'not_found' || r.state === 'error'
              ? 'not found'
              : !r.literary
                ? 'its literary genre'
                : 'check this row'
      }`;
  return (
    <ListRow
      class={`ai-row${ready(r) || (r.state === 'on_list' && r.confirm) ? ' ok' : ''}`}
      data-row={r.seq}
      need={need}
      quiet={r.state === 'skipped'}
      cells={[
      <span class="ai-line">
        {r.text}
      </span>,
      <span class="ai-book">
        {r.found ? (
          <>
            {r.found.coverUrl ? <img class="ai-cover" src={r.found.coverUrl} alt="" loading="lazy" /> : <span class="ai-cover" aria-hidden="true" />}
            <span>
              <span class="ai-title">{r.found.title}</span>
              <span class="ai-quiet ai-by">{foundLine(r.found)}</span>
            </span>
          </>
        ) : r.state === 'on_list' ? (
          <span>
            <span class="ai-title">{tidyCase(r.title)}</span>
            <span class="ai-quiet ai-by">
              On your list as {b}
              {r.have?.length ? `: ${r.have.join(', ')}, owned` : ''}
            </span>
          </span>
        ) : r.state === 'pick' ? (
          <span>
            <span class="ai-title">{tidyCase(r.title)}</span>
            <span class="ai-quiet ai-by">{r.candidates.length > 1 ? `${r.candidates.length} books found` : '1 book found, not quite this'}</span>
          </span>
        ) : r.state === 'not_found' || r.state === 'error' ? (
          <span>
            <span class="ai-title">No match found</span>
            <span class="ai-quiet ai-by">Searched Open Library and Google Books</span>
          </span>
        ) : r.state === 'skipped' ? (
          <span class="ai-title">{tidyCase(r.title)}</span>
        ) : null}
      </span>,
      <span class="ai-check">
        {r.state === 'on_list' ? <span class="ai-checkhead">{pill}</span> : pill ? <span class={`ai-pill${needs ? ' need' : ''}`}>{pill}</span> : null}
        {r.state === 'matched' ? (
          <>
            {' '}
            {r.candidates.length ? (
              <button type="button" class="btn-link" onClick={() => patch({ state: 'pick', candidates: [r.found!, ...r.candidates], found: null })}>
                Not this one?
              </button>
            ) : (
              <button type="button" class="btn-link" onClick={() => setSearch(queryOf(r))}>
                Not this one?
              </button>
            )}
            {!r.literary ? (
              <ChoiceChips
                class="ai-lit need"
                label={`Literary genre for ${r.found?.title ?? r.title}`}
                small
                lead={<span class="ai-lit-q">Literary genre?</span>}
                choices={LITERARY.map((l) => [l.key, l.label] as const)}
                value={null}
                onPick={(k) => k && patch({ literary: k })}
              />
            ) : (
              <span class="ai-quiet"> · {LITERARY.find((l) => l.key === r.literary)!.label}</span>
            )}
            {searchBox}
          </>
        ) : r.state === 'on_list' ? (
          !r.confirm ? (
            <>
              <span class="ai-checknote">Already on your list. Add anything?</span>
              <span class="ai-picks">
                <button type="button" class="btn-book" onClick={() => patch({ confirm: 'add', copy: r.copy === 'none' ? 'paper' : r.copy })}>
                  {r.copy === 'none' ? 'Add a copy' : `Add ${copyPhrase(r.copy)}`}
                </button>
                <button type="button" class="btn-secondary" onClick={() => patch({ confirm: 'keep' })}>
                  Leave mine as it is
                </button>
              </span>
            </>
          ) : (
            <>
              <span class="ai-checknote">{r.confirm === 'add' ? (r.copy === 'none' ? 'Pick the copy to add' : `Confirmed: adds ${copyPhrase(r.copy)}`) : 'Confirmed: no change'}</span>{' '}
              <button type="button" class="btn-link" onClick={() => patch({ confirm: null })}>
                Change
              </button>
            </>
          )
        ) : r.state === 'pick' ? (
          <>
            <span class="ai-checknote">Which one did you mean?</span>
            <span class="ai-picks" role="group" aria-label={`Which book is ${r.title}?`}>
              {(showMore ? [...r.candidates, ...extra] : r.candidates).map((c) => (
                <button key={c.key} type="button" class="btn-secondary" onClick={() => pick(c)}>
                  {c.title !== r.title ? `${c.title} · ` : ''}
                  {foundLine(c) || c.source}
                  {langTag(c)}
                </button>
              ))}
            </span>
            <span class="ai-quiet">
              {extra.length && !showMore ? (
                <>
                  <button type="button" class="btn-link" onClick={() => setShowMore(true)}>
                    Show {num(extra.length)} more
                  </button>{' '}
                  ·{' '}
                </>
              ) : null}
              <button type="button" class="btn-link" onClick={() => setSearch(queryOf(r))}>
                None of these: search again
              </button>{' '}
              ·{' '}
              <button type="button" class="btn-link" onClick={() => patch({ state: 'skipped' })}>
                Skip
              </button>
            </span>
            {searchBox}
          </>
        ) : r.state === 'not_found' || r.state === 'error' ? (
          <>
            <span class="ai-checknote">{r.state === 'error' ? r.error : 'Nothing close enough.'}</span>
            {/* The row's only ways forward, so buttons, not links (consistency pass, R5). */}
            <span class="ai-picks">
              <button type="button" class="btn-secondary" onClick={() => setSearch(queryOf(r))}>
                Search again
              </button>
              <button type="button" class="btn-secondary" onClick={() => patch({ state: 'skipped' })}>
                Skip
              </button>
            </span>
            {searchBox}
          </>
        ) : r.state === 'skipped' ? (
          <>
            <span class="ai-checknote">Won’t be imported.</span>{' '}
            <button type="button" class="btn-link" onClick={() => patch({ state: r.onListBook ? 'on_list' : r.found ? 'matched' : r.candidates.length ? 'pick' : 'not_found' })}>
              Undo skip
            </button>
          </>
        ) : null}
      </span>,
      <span class="ai-copy">
        {r.state === 'skipped' ? (
          <span class="ai-quiet">–</span>
        ) : (
          // A value in a row: "Paper ▾", dashed "Not owned yet ▾" while empty (consistency pass, R4).
          <PillMenu
            label={copyLabel(r.copy)}
            ariaLabel={`Copy of ${r.title}`}
            sheetTitle={`Copy of ${tidyCase(r.title)}`}
            empty={r.copy === 'none'}
            choices={IMPORT_COPIES.map((c) => ({ value: c.key, label: c.label }))}
            current={r.copy}
            onPick={(v: ImportCopy) => patch({ copy: v })}
          />
        )}
      </span>,
      <span class="ai-goes">
        {goes}
      </span>,
      ]}
    />
  );
}
